import React, { RefObject } from 'react';
import { Department, StaffMember } from '../../lib/types';
import { inp, sel, MsgBox } from './shared';

const STAFF_PAGE_SIZE = 10;

interface Props {
  isOrgAdmin: boolean;
  activeDepts: Department[];
  activeStaff: StaffMember[];
  filteredStaff: StaffMember[];

  userCount?: number;
  maxUsers?: number;
  orgName?: string;

  invEmail: string; setInvEmail: (s: string) => void;
  invName: string; setInvName: (s: string) => void;
  invDepts: string[]; setInvDepts: React.Dispatch<React.SetStateAction<string[]>>;
  invRole: string; setInvRole: (s: string) => void;
  invBusy: boolean;
  invMsg: string;
  onInvite: () => void;

  newStaffName: string; setNewStaffName: (s: string) => void;
  newStaffEmail: string; setNewStaffEmail: (s: string) => void;
  newStaffDept: string; setNewStaffDept: (s: string) => void;
  staffBusy: boolean;
  staffMsg: string;
  onAddStaff: () => void;

  csvRef: RefObject<HTMLInputElement>;
  onCSVImport: (e: React.ChangeEvent<HTMLInputElement>) => void;

  staffSearch: string;
  staffPage: number; setStaffPage: React.Dispatch<React.SetStateAction<number>>;
  onSearchChange: (s: string) => void;
  onDeactivateStaff: (id: string, name: string) => void;
}

function RoleBadge({ role }: { role?: string }) {
  if (!role) return null;
  const color = role === 'org_admin' ? 'var(--purple)' : role === 'dept_admin' ? 'var(--blue)' : 'var(--text2)';
  const bg    = role === 'org_admin' ? 'var(--purple-bg)' : role === 'dept_admin' ? 'var(--blue-bg)' : 'var(--surface2)';
  const label = role === 'org_admin' ? 'Account Owner' : role === 'dept_admin' ? 'Team Member' : role;
  return <span style={{ fontSize: 10, fontWeight: 600, color, background: bg, borderRadius: 4, padding: '2px 6px' }}>{label}</span>;
}

export function SettingsTeam(p: Props) {
  const thStyle: React.CSSProperties = { fontSize: 11, fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', padding: '6px 8px', textAlign: 'left', borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap' };
  const tdStyle: React.CSSProperties = { fontSize: 12, padding: '8px 8px', borderBottom: '1px solid var(--border)', verticalAlign: 'middle', maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };

  const rows = p.staffSearch.trim() ? p.filteredStaff : p.activeStaff;
  const totalPages = Math.max(1, Math.ceil(rows.length / STAFF_PAGE_SIZE));
  const safePage = Math.min(p.staffPage, totalPages);
  const pageRows = rows.slice((safePage - 1) * STAFF_PAGE_SIZE, safePage * STAFF_PAGE_SIZE);
  const deptMap = Object.fromEntries(p.activeDepts.map(d => [d.id, d.name]));

  return (
    <div style={{ animation: 'fadeSlideIn .25s ease-out' }}>
      {p.isOrgAdmin && p.userCount !== undefined && p.maxUsers !== undefined && (
        <div style={{ background: 'var(--bg)', borderRadius: 10, padding: 14, marginBottom: 20, border: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)', marginBottom: 2 }}>
              Seats
            </div>
            <div style={{ fontSize: 12, color: 'var(--text3)' }}>
              {p.userCount} of {p.maxUsers} seats used
            </div>
          </div>
          <a
            className="hbtn"
            href={`mailto:hello@getgopal.co.nz?subject=${encodeURIComponent(`More seats request${p.orgName ? ' — ' + p.orgName : ''}`)}&body=${encodeURIComponent(`Hi GoPal,\n\nI'd like to add more seats to my account${p.orgName ? ' (' + p.orgName + ')' : ''}.\n\nCurrent: ${p.userCount} of ${p.maxUsers} seats used.\nNew total seats needed: \n\nThanks.`)}`}
            style={{ borderRadius: 8, padding: '8px 16px', fontSize: 12, fontWeight: 600, textDecoration: 'none' }}
          >
            Request more seats
          </a>
        </div>
      )}

      <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--text)', marginBottom: 4 }}>Team</div>
      <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 20 }}>Invite team members and manage the staff directory.</div>

      {p.isOrgAdmin && (
        <div style={{ background: 'var(--bg)', borderRadius: 10, padding: 14, marginBottom: 20, border: '1px solid var(--border)' }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)', marginBottom: 10 }}>Invite a team member</div>
          <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 10 }}>They will receive an email with a login link.</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8 }}>
            <input value={p.invEmail} onChange={e => p.setInvEmail(e.target.value)} placeholder="Email address *" type="email" style={inp} />
            <input value={p.invName} onChange={e => p.setInvName(e.target.value)} placeholder="Full name" style={inp} />
            <select value={p.invRole} onChange={e => { p.setInvRole(e.target.value); p.setInvDepts([]); }} style={sel}>
              <option value="dept_admin">Team Member</option>
              <option value="org_admin">Account Owner</option>
            </select>
            {p.invRole === 'dept_admin' && (
              <div style={{ gridColumn: '1 / -1' }}>
                <div style={{ fontSize: 11, fontWeight: 500, color: 'var(--text2)', marginBottom: 6 }}>Departments *</div>
                {p.activeDepts.length === 0 ? (
                  <div style={{ fontSize: 12, color: 'var(--text3)' }}>No departments</div>
                ) : (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {p.activeDepts.map(d => {
                      const selected = p.invDepts.includes(d.id);
                      return (
                        <button key={d.id} type="button"
                          onClick={() => p.setInvDepts(prev => selected ? prev.filter(id => id !== d.id) : [...prev, d.id])}
                          style={{ background: selected ? 'var(--accent)' : 'var(--surface2)', border: `1px solid ${selected ? 'var(--accent)' : 'var(--border)'}`, color: selected ? 'white' : 'var(--text2)', borderRadius: 20, padding: '5px 14px', fontSize: 12, cursor: 'pointer', fontFamily: 'var(--font)', transition: 'all 0.15s' }}>
                          {d.name}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
          <MsgBox msg={p.invMsg} />
          <button className="hbtn acc" disabled={p.invBusy || !p.invEmail.trim()} onClick={p.onInvite}
            style={{ borderRadius: 8, padding: '8px 18px', fontSize: 12, fontWeight: 600 }}>
            {p.invBusy ? 'Sending...' : 'Send invite'}
          </button>
        </div>
      )}

      <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)', marginBottom: 10 }}>Staff directory</div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
        <input value={p.newStaffName} onChange={e => p.setNewStaffName(e.target.value)} placeholder="Full name *" style={{ ...inp, flex: 1 }} />
        <input value={p.newStaffEmail} onChange={e => p.setNewStaffEmail(e.target.value)} placeholder="Email" style={{ ...inp, flex: 1 }} />
        <select value={p.newStaffDept} onChange={e => p.setNewStaffDept(e.target.value)} style={{ ...sel, flex: 1 }}>
          <option value="">-- Dept --</option>
          {p.activeDepts.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
        </select>
        <button className="hbtn acc" disabled={p.staffBusy || !p.newStaffName.trim()} onClick={p.onAddStaff}
          style={{ borderRadius: 8, padding: '8px 14px', fontSize: 12, whiteSpace: 'nowrap' }}>+ Add</button>
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 10, alignItems: 'center' }}>
        <input ref={p.csvRef} type="file" accept=".csv,.txt" onChange={p.onCSVImport} disabled={p.staffBusy}
          style={{ fontSize: 11, fontFamily: 'var(--font)', flex: 1 }} />
        <span style={{ fontSize: 10, color: 'var(--text3)', whiteSpace: 'nowrap' }}>CSV: Name, Short, Dept, Email, Role</span>
      </div>
      <MsgBox msg={p.staffMsg} />

      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
        <input value={p.staffSearch} onChange={e => p.onSearchChange(e.target.value)} placeholder="Search staff..."
          style={{ ...inp, flex: 1, padding: '6px 10px', fontSize: 12 }} />
      </div>

      {p.activeStaff.length === 0 ? (
        <div style={{ color: 'var(--text3)', fontSize: 12, textAlign: 'center', padding: '20px 0' }}>No staff yet. Add individually or import CSV.</div>
      ) : (
        <>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={thStyle}>Name</th>
                  <th style={thStyle}>Email</th>
                  <th style={thStyle}>Role</th>
                  <th style={thStyle}>Department</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {pageRows.map(s => (
                  <tr key={s.id}>
                    <td style={{ ...tdStyle, fontWeight: 500 }}>{s.name}</td>
                    <td style={{ ...tdStyle, color: 'var(--text2)' }}>{s.email || '—'}</td>
                    <td style={tdStyle}><RoleBadge role={s.role} /> {!s.role && <span style={{ color: 'var(--text3)' }}>—</span>}</td>
                    <td style={{ ...tdStyle, color: 'var(--text2)' }}>{s.department_id ? (deptMap[s.department_id] || '—') : '—'}</td>
                    <td style={{ ...tdStyle, textAlign: 'right' }}>
                      <button onClick={() => p.onDeactivateStaff(s.id, s.name)}
                        style={{ background: 'none', border: '1px solid var(--border)', color: 'var(--text3)', padding: '3px 8px', borderRadius: 6, cursor: 'pointer', fontSize: 10, fontFamily: 'var(--font)' }}>
                        Deactivate
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 10 }}>
            <span style={{ fontSize: 11, color: 'var(--text3)' }}>
              Showing {rows.length === 0 ? 0 : (safePage - 1) * STAFF_PAGE_SIZE + 1}–{Math.min(safePage * STAFF_PAGE_SIZE, rows.length)} of {rows.length}
            </span>
            <div style={{ display: 'flex', gap: 6 }}>
              <button disabled={safePage <= 1} onClick={() => p.setStaffPage(n => n - 1)}
                style={{ background: 'none', border: '1px solid var(--border)', color: safePage <= 1 ? 'var(--text3)' : 'var(--text)', padding: '4px 10px', borderRadius: 6, cursor: safePage <= 1 ? 'default' : 'pointer', fontSize: 11, fontFamily: 'var(--font)' }}>Prev</button>
              <button disabled={safePage >= totalPages} onClick={() => p.setStaffPage(n => n + 1)}
                style={{ background: 'none', border: '1px solid var(--border)', color: safePage >= totalPages ? 'var(--text3)' : 'var(--text)', padding: '4px 10px', borderRadius: 6, cursor: safePage >= totalPages ? 'default' : 'pointer', fontSize: 11, fontFamily: 'var(--font)' }}>Next</button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
